/**
 * DamageClaimDetailPage — Karteritesi igeny reszletei
 * Epic 69: Garancia RMA & Cseregep
 *
 * Reszletes nezet: statusz, osszegek, cseregep kiadas.
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useApproveDamageClaim,
  useDamageClaim,
  useRejectDamageClaim,
} from '@/hooks/useDamageClaims';
import { useTranslation } from '@kgc/i18n';
import { useNavigate, useParams } from 'react-router-dom';

const STATUS_LABEL_KEYS: Record<string, string> = {
  PENDING: 'service.damageClaim.statusLabels.pending',
  APPROVED: 'service.damageClaim.statusLabels.approved',
  REJECTED: 'service.damageClaim.statusLabels.rejected',
  SETTLED: 'service.damageClaim.statusLabels.settled',
};

const TYPE_LABEL_KEYS: Record<string, string> = {
  GAR: 'service.damageClaim.typeLabels.warranty',
  BH: 'service.damageClaim.typeLabels.damage',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function DamageClaimDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { claim, isLoading, error, refetch } = useDamageClaim(id);
  const approveMutation = useApproveDamageClaim();
  const rejectMutation = useRejectDamageClaim();

  const handleApprove = async () => {
    if (!id) return;
    await approveMutation.mutateAsync(id);
    refetch();
  };

  const handleReject = async () => {
    if (!id) return;
    const note = prompt(t('service.damageClaim.rejectionPrompt'));
    if (!note) return;
    await rejectMutation.mutateAsync({ id, rejectionNote: note });
    refetch();
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !claim) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-red-500">{error ?? t('service.damageClaim.notFound')}</p>
            <Button className="mt-4" onClick={() => navigate('/worksheet/damage-claims')}>
              {t('common.backToList')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('service.damageClaim.titleSingle')}</h1>
          <p className="text-muted-foreground">#{claim.id.slice(0, 8)}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/worksheet/damage-claims')}>
          {t('common.back')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Status & Type */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t('service.damageClaim.basicData')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('service.damageClaim.statusLabel')}</span>
              <span className="font-medium">
                {STATUS_LABEL_KEYS[claim.status]
                  ? t(STATUS_LABEL_KEYS[claim.status])
                  : claim.status}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('service.damageClaim.typeLabel')}</span>
              <span>
                {TYPE_LABEL_KEYS[claim.type] ? t(TYPE_LABEL_KEYS[claim.type]) : claim.type}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('service.damageClaim.rentalId')}</span>
              <span className="font-mono text-sm">{claim.rentalId.slice(0, 12)}...</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('service.damageClaim.createdAt')}</span>
              <span>{new Date(claim.createdAt).toLocaleDateString('hu-HU')}</span>
            </div>
          </CardContent>
        </Card>

        {/* Financials */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t('service.damageClaim.financialData')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t('service.damageClaim.repairCostLabel')}
              </span>
              <span>{formatCurrency(claim.repairCost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t('service.damageClaim.lostRevenueLabel')}
              </span>
              <span>{formatCurrency(claim.lostRevenue)}</span>
            </div>
            <div className="flex justify-between border-t pt-2 font-bold">
              <span>{t('service.damageClaim.totalClaimLabel')}</span>
              <span>{formatCurrency(claim.totalClaim)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {claim.note && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t('common.notes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{claim.note}</p>
          </CardContent>
        </Card>
      )}

      {claim.rejectionNote && (
        <Card className="mb-4 border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-red-600">
              {t('service.damageClaim.rejectionReason')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-600">{claim.rejectionNote}</p>
          </CardContent>
        </Card>
      )}

      {claim.approvedBy && (
        <Card className="mb-4 border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-green-600">
              {t('service.damageClaim.approval')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              {t('service.damageClaim.approvedBy', { name: claim.approvedBy })}
              {claim.approvedAt && ` — ${new Date(claim.approvedAt).toLocaleDateString('hu-HU')}`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      {claim.status === 'PENDING' && (
        <div className="flex gap-3">
          <Button onClick={handleApprove} disabled={approveMutation.isPending}>
            {approveMutation.isPending
              ? t('service.damageClaim.processing')
              : t('service.damageClaim.approve')}
          </Button>
          <Button variant="destructive" onClick={handleReject} disabled={rejectMutation.isPending}>
            {rejectMutation.isPending
              ? t('service.damageClaim.processing')
              : t('service.damageClaim.reject')}
          </Button>
        </div>
      )}
    </div>
  );
}
